import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Cropper, { type Area } from "react-easy-crop";
import { Crop, X } from "lucide-react";
import "react-easy-crop/react-easy-crop.css";
import { Button } from "@/components/atoms/Button";
import { useModalDismiss } from "@/hooks/useModalDismiss";
import { createCroppedLogoFile, LOGO_CROP_OUTPUT_SIZE } from "@/lib/cropImage";

interface Props {
  open: boolean;
  imageSrc: string;
  fileName: string;
  fileType: string;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (file: File) => void | Promise<void>;
}

export function LogoCropModal({
  open,
  imageSrc,
  fileName,
  fileType,
  busy = false,
  onCancel,
  onConfirm,
}: Props) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { onOverlayClick } = useModalDismiss(open && !busy && !submitting, onCancel);

  useEffect(() => {
    if (!open) return;
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setError(null);
  }, [open, imageSrc]);

  const onCropComplete = useCallback((_croppedArea: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels || busy || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const file = await createCroppedLogoFile(imageSrc, croppedAreaPixels, fileName, fileType);
      await onConfirm(file);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not crop the selected image.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const working = busy || submitting;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-ink-950/40 p-4 backdrop-blur-sm"
      onClick={onOverlayClick}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="logo-crop-title"
        className="flex w-full max-w-lg flex-col overflow-hidden rounded-xl border border-bridge-border bg-white shadow-premium"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-bridge-border/60 px-6 py-5">
          <div className="flex min-w-0 items-start gap-3">
            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-bridge-accent/10 text-bridge-accent">
              <Crop size={20} />
            </div>
            <div className="min-w-0">
              <h2 id="logo-crop-title" className="text-lg font-bold text-bridge-fg">
                Crop logo
              </h2>
              <p className="mt-1 text-sm text-bridge-muted">
                Drag to reposition and use the slider to zoom. Output is saved as a{" "}
                {LOGO_CROP_OUTPUT_SIZE}x{LOGO_CROP_OUTPUT_SIZE} square.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={working}
            className="shrink-0 rounded-lg p-1.5 text-bridge-muted transition-colors hover:bg-bridge-surface hover:text-bridge-fg disabled:opacity-40"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="relative h-72 bg-ink-900">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="rect"
            showGrid
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="space-y-3 px-6 py-4">
          <label className="block text-xs font-semibold uppercase tracking-wide text-bridge-muted">
            Zoom
          </label>
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            disabled={working}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-full accent-bridge-accent"
          />
          {error ? <p className="text-sm text-red-600">{error}</p> : null}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-bridge-border/60 bg-bridge-bg/50 px-6 py-4">
          <Button type="button" variant="secondary" size="sm" onClick={onCancel} disabled={working}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            loading={working}
            disabled={working || !croppedAreaPixels}
            onClick={() => void handleConfirm()}
          >
            {working ? "Saving..." : "Apply and upload"}
          </Button>
        </div>
      </section>
    </div>,
    document.body,
  );
}
